/**
 * Sort Selector — Y2K brutalist dropdown that replaces the native <select>.
 *
 * Markup contract (rendered inside <sort-selector>):
 *   <input type="hidden" name="sort_by" value="..." data-sort-input>
 *   <button data-sort-trigger> ... <span data-sort-current>...</span> ... </button>
 *   <ul data-sort-panel hidden>
 *     <li><button data-sort-option data-value="manual">...</button></li>
 *     ...
 *   </ul>
 *
 * Behavior:
 *   - Trigger toggles the panel.
 *   - Clicking an option sets the hidden input value, updates the label, closes
 *     the panel, dispatches a "change" event on the input so the enclosing
 *     <collection-filters> auto-submit picks it up.
 *   - Esc / click-outside closes the panel.
 */

class SortSelector extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector("[data-sort-input]");
    this.trigger = this.querySelector("[data-sort-trigger]");
    this.panel = this.querySelector("[data-sort-panel]");
    this.label = this.querySelector("[data-sort-current]");
    if (!this.input || !this.trigger || !this.panel) return;

    this.trigger.addEventListener("click", (e) => {
      e.stopPropagation();
      this.#toggle();
    });

    this.querySelectorAll("[data-sort-option]").forEach((opt) =>
      opt.addEventListener("click", (e) => {
        e.stopPropagation();
        this.#select(opt.dataset.value, opt.textContent.trim());
      }),
    );

    document.addEventListener("click", (e) => {
      if (!this.contains(e.target)) this.#close();
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.#close();
    });
  }

  #toggle() {
    if (this.panel.hidden) this.#open();
    else this.#close();
  }

  #open() {
    this.panel.hidden = false;
    this.trigger.setAttribute("aria-expanded", "true");
  }

  #close() {
    this.panel.hidden = true;
    this.trigger.setAttribute("aria-expanded", "false");
  }

  /**
   * Adopt a sort value chosen elsewhere — <collection-filters> calls this
   * after a filter swap, since clearing filters resets sorting server-side.
   * Silent on purpose: it reflects a result already rendered, so firing
   * `change` here would submit the form again.
   */
  sync(value) {
    if (!this.input) return;
    const opt = [...this.querySelectorAll("[data-sort-option]")].find(
      (o) => o.dataset.value === value,
    );
    // Apply even when the hidden input already holds this value: something
    // else may have written it straight onto the input (the collection's
    // default-sort bootstrap does), which leaves the label and the option
    // rows showing the sort the page was rendered with.
    this.#applyState(value, opt?.textContent.trim());
  }

  #select(value, label) {
    if (this.input.value === value) {
      this.#close();
      return;
    }
    this.#applyState(value, label);
    this.#close();
    this.input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  #applyState(value, label) {
    this.input.value = value;
    if (this.label && label) this.label.textContent = label;
    this.querySelectorAll("[data-sort-option]").forEach((opt) => {
      const active = opt.dataset.value === value;
      // The selected option is painted by Liquid with these utilities beside
      // `is-active`, so all three have to move together or the previously
      // selected row keeps its pink fill until the next full page load.
      opt.classList.toggle("is-active", active);
      opt.classList.toggle("bg-hophop-pink", active);
      opt.classList.toggle("text-pink-deep", active);
      opt.setAttribute("aria-selected", String(active));
    });
  }
}

customElements.define("sort-selector", SortSelector);

export { SortSelector };
